export const CUSTOMER_SITUATION = {
  NEW: "new",
  PAR360_ADDON: "par360Addon",
  MIXED: "mixed",
  SCALE_EXPANSION: "scaleExpansion",
} as const;

export type CustomerSituation =
  (typeof CUSTOMER_SITUATION)[keyof typeof CUSTOMER_SITUATION];

export const FACILITY_STATUS = {
  UNSCALED: "unscaled",
  ALREADY_SCALED: "alreadyScaled",
} as const;

export type FacilityStatus =
  (typeof FACILITY_STATUS)[keyof typeof FACILITY_STATUS];

export const FACILITY_STATUS_KEY = "Facility Status";
export const EXISTING_SCALE_COUNT_KEY = "Existing Scale Count";
export const NEW_SCALE_COUNT_KEY = "New Scale Count";
export const SCALE_COUNT_KEY = "Scale Count";
export const TOTAL_SKUS_KEY = "Total SKUs";
export const SCALE_HARDWARE_CAPEX_KEY = "Scale Hardware (CAPEX)";
export const SKU_REDUCTION_KEY = "SKU Reduction from Non-Mover Elimination";

export type FacilityScaleSplit = {
  status: FacilityStatus;
  existingScales: number;
  newScales: number;
  totalScales: number;
};

const toCount = (value: unknown): number => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
};

export const resolveCustomerSituation = (client?: {
  customerSituation?: string;
  isPar360AddOn?: boolean;
} | null): CustomerSituation => {
  const raw = String(client?.customerSituation ?? "").trim();
  if (
    raw === CUSTOMER_SITUATION.NEW ||
    raw === CUSTOMER_SITUATION.PAR360_ADDON ||
    raw === CUSTOMER_SITUATION.MIXED ||
    raw === CUSTOMER_SITUATION.SCALE_EXPANSION
  ) {
    return raw;
  }
  return client?.isPar360AddOn
    ? CUSTOMER_SITUATION.PAR360_ADDON
    : CUSTOMER_SITUATION.NEW;
};

export const isExistingCustomerMode = (
  situation: CustomerSituation
): boolean => situation !== CUSTOMER_SITUATION.NEW;

export const defaultFacilityStatus = (
  situation: CustomerSituation
): FacilityStatus => {
  if (
    situation === CUSTOMER_SITUATION.PAR360_ADDON ||
    situation === CUSTOMER_SITUATION.SCALE_EXPANSION
  ) {
    return FACILITY_STATUS.ALREADY_SCALED;
  }
  return FACILITY_STATUS.UNSCALED;
};

export const resolveFacilityScales = (
  facility: Record<string, any> | null | undefined,
  situation: CustomerSituation
): FacilityScaleSplit => {
  const legacy = toCount(facility?.[SCALE_COUNT_KEY]);
  const existingEntered = facility?.[EXISTING_SCALE_COUNT_KEY];
  const newEntered = facility?.[NEW_SCALE_COUNT_KEY];
  const hasExisting = existingEntered !== undefined && existingEntered !== "";
  const hasNew = newEntered !== undefined && newEntered !== "";

  if (situation === CUSTOMER_SITUATION.NEW) {
    return {
      status: FACILITY_STATUS.UNSCALED,
      existingScales: 0,
      newScales: legacy,
      totalScales: legacy,
    };
  }

  const rawStatus = String(facility?.[FACILITY_STATUS_KEY] ?? "");
  const status: FacilityStatus =
    rawStatus === FACILITY_STATUS.ALREADY_SCALED ||
    rawStatus === FACILITY_STATUS.UNSCALED
      ? rawStatus
      : defaultFacilityStatus(situation);

  if (situation === CUSTOMER_SITUATION.PAR360_ADDON) {
    const existingScales = hasExisting ? toCount(existingEntered) : 0;
    const leftoverNew = hasNew ? toCount(newEntered) : 0;
    const installed = existingScales + leftoverNew || legacy;
    return {
      status: FACILITY_STATUS.ALREADY_SCALED,
      existingScales: installed,
      newScales: 0,
      totalScales: installed,
    };
  }

  if (status === FACILITY_STATUS.UNSCALED) {
    const newScales = hasNew ? toCount(newEntered) : legacy;
    return {
      status,
      existingScales: 0,
      newScales,
      totalScales: newScales,
    };
  }

  const existingScales = hasExisting ? toCount(existingEntered) : legacy;
  const newScales = toCount(newEntered);

  return {
    status,
    existingScales,
    newScales,
    totalScales: existingScales + newScales,
  };
};

// Share (0–1) of an already-scaled facility's SKUs on existing scales. Their
// overstock/expiration savings are already in the customer's baseline, so
// PAR 360 is credited only on the remaining share of supply spend.
export const existingScaleCoverage = (
  facility: Record<string, any> | null | undefined,
  situation: CustomerSituation
): number => {
  if (situation === CUSTOMER_SITUATION.NEW) return 0;
  const split = resolveFacilityScales(facility, situation);
  if (split.status !== FACILITY_STATUS.ALREADY_SCALED) return 0;
  const skus = toCount(facility?.[TOTAL_SKUS_KEY]);
  if (skus <= 0 || split.existingScales <= 0) return 0;
  return Math.min(1, split.existingScales / skus);
};

export const sumFacilityScales = (
  spendRows: Record<string, any> | null | undefined,
  situation: CustomerSituation
): { existingScales: number; newScales: number; totalScales: number } => {
  const keys = Object.keys(spendRows ?? {}).filter((k) => k.startsWith("row"));
  return keys.reduce(
    (acc, key) => {
      const split = resolveFacilityScales(spendRows?.[key], situation);
      acc.existingScales += split.existingScales;
      acc.newScales += split.newScales;
      acc.totalScales += split.totalScales;
      return acc;
    },
    { existingScales: 0, newScales: 0, totalScales: 0 }
  );
};

export const situationTitle = (situation: CustomerSituation): string => {
  switch (situation) {
    case CUSTOMER_SITUATION.PAR360_ADDON:
      return "Incremental PAR 360 ROI";
    case CUSTOMER_SITUATION.MIXED:
      return "Existing + New Facility ROI";
    case CUSTOMER_SITUATION.SCALE_EXPANSION:
      return "Scale Expansion + PAR 360 ROI";
    default:
      return "ROI Calculations";
  }
};
